'use client';

import { useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import FixturesSearchBar, {
  FixturesSearchState,
  SearchOption,
} from '@components/organisms/fixtures-search-bar';
import RefereeAppointmentListing from '@components/organisms/referee-appointment-listing';
import { LoadingTemplate } from '@components/templates/loading-template';
import { Button } from '@components/v3/atoms/button';
import { DEFAULT_LOCALE, Locale } from '@core/constants/locales.constants';
import { makeCometTeamCrestResolver } from '@utils/football';
import dayjs from '@lib/dayjs';
import {
  RefereeSearchCriteria,
  useCometTeamCrests,
  useRefereeAssignments,
  useRefereeTeams,
} from './hooks/refereeQueries';
import { refereeFixtureToCardProps } from './utils/refereeFixture.transform';
import { RefereeAppointmentsSearchProps } from './refereeAppointmentsSearch.types';
import styles from './refereeAppointmentsSearch.module.scss';

const toDateParam = (locale: Locale, date?: Date) =>
  date ? dayjs(locale)(date).format('YYYY-MM-DD') : undefined;

const RefereeAppointmentsSearchInner = ({
  competitions,
  fallbackClubCrest,
  locale = DEFAULT_LOCALE,
  labels,
  testId = 'referee-appointments-search',
}: RefereeAppointmentsSearchProps) => {
  // Competition drives the dependent team query; criteria (set on Search) drives
  // the paginated results.
  const [competitionId, setCompetitionId] = useState<string | undefined>();
  const [criteria, setCriteria] = useState<RefereeSearchCriteria | null>(null);

  const teamsQuery = useRefereeTeams(competitionId);
  const assignmentsQuery = useRefereeAssignments(criteria);

  const clubs = useMemo<SearchOption[]>(
    () =>
      (teamsQuery.data ?? []).map((team) => ({
        id: String(team.teamId),
        text: team.teamName,
      })),
    [teamsQuery.data]
  );

  const fixtures = useMemo(
    () =>
      (assignmentsQuery.data?.pages ?? []).flatMap(
        (page) => page?.results ?? []
      ),
    [assignmentsQuery.data]
  );

  // Unique team ids across all loaded pages → one Contentful crest lookup.
  const teamIds = useMemo(
    () =>
      Array.from(
        new Set(
          fixtures.flatMap((fixture) => [
            String(fixture.homeTeamId),
            String(fixture.awayTeamId),
          ])
        )
      ),
    [fixtures]
  );

  const crestsQuery = useCometTeamCrests(teamIds);

  const resolveCrest = useMemo(
    () => makeCometTeamCrestResolver(crestsQuery.data ?? [], fallbackClubCrest),
    [crestsQuery.data, fallbackClubCrest]
  );

  const appointments = useMemo(
    () =>
      fixtures.map((fixture) => ({
        ...refereeFixtureToCardProps(fixture, resolveCrest, locale),
        dateTbcLabel: labels?.dateTbc,
      })),
    [fixtures, resolveCrest, locale, labels?.dateTbc]
  );

  const handleSearch = ({
    competition,
    dateRange,
    club,
  }: FixturesSearchState) =>
    setCriteria({
      competitionId: competition?.id || undefined,
      teamId: club?.id || undefined,
      from: toDateParam(locale, dateRange?.from),
      to: toDateParam(locale, dateRange?.to),
    });

  const isSearching = Boolean(criteria) && assignmentsQuery.isLoading;
  const noResults =
    Boolean(criteria) &&
    !assignmentsQuery.isLoading &&
    appointments.length === 0;

  return (
    <section className={styles.refereeAppointmentsSearch} data-testid={testId}>
      <FixturesSearchBar
        competitions={competitions}
        clubs={clubs}
        clubPlaceholder={
          teamsQuery.isFetching
            ? (labels?.clubLoading ?? 'Loading clubs…')
            : undefined
        }
        onCompetitionChange={(competition) =>
          setCompetitionId(competition?.id || undefined)
        }
        onSearch={handleSearch}
      />

      {isSearching && (
        <LoadingTemplate label={labels?.loading ?? 'Loading fixtures…'} />
      )}

      {noResults && (
        <p className={styles.refereeAppointmentsSearch__empty}>
          {labels?.noResults ?? 'No referee appointments found.'}
        </p>
      )}

      {appointments.length > 0 && (
        <RefereeAppointmentListing
          appointments={appointments}
          footer={
            assignmentsQuery.hasNextPage ? (
              <Button
                text={labels?.loadMore ?? 'Load more'}
                variant="primary"
                onClick={() => assignmentsQuery.fetchNextPage()}
                disabled={assignmentsQuery.isFetchingNextPage}
                nativeProps={{ type: 'button' }}
              />
            ) : undefined
          }
        />
      )}
    </section>
  );
};

/**
 * Referee Appointments search feature — composes FixturesSearchBar +
 * RefereeAppointmentListing over the Comet API. Owns its own React Query client
 * so the feature is self-contained wherever the content block mounts it.
 */
const RefereeAppointmentsSearch = (props: RefereeAppointmentsSearchProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1, refetchOnWindowFocus: false },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <RefereeAppointmentsSearchInner {...props} />
    </QueryClientProvider>
  );
};

export default RefereeAppointmentsSearch;
